<template>
  <div>
    <v-tabs v-model="tab" color="realEstate">
      <v-tab @click="fetchPending">
        {{ $t('hausefulInvoices.pending') }}
      </v-tab>
      <v-tab @click="fetchInvoices('AWAITING_APPROVAL')">
        {{ $t('hausefulInvoices.waitingApproval') }}
      </v-tab>
      <v-tab @click="fetchInvoices('REFUSED')">
        {{ $t('hausefulInvoices.refused') }}
      </v-tab>
      <v-tab @click="fetchInvoices('APPROVED')">
        {{ $t('hausefulInvoices.approved') }}
      </v-tab>
      <v-tab @click="transfers()">
        {{ $t('hausefulInvoices.paid') }}
      </v-tab>

      <div class="tabs__filters">
        <AdminPayInvoicesButton v-if="showPayInfo" />
        <AdminPendingInvoicesFilters v-if="showPendingInvoiceFilters" ref="adminPendingInvoicesFilters" />
        <AdminInvoicesFilters v-if="showInvoiceFilters" ref="adminInvoicesFilters" />
        <AdminTransfersFilters v-if="showTransferFilters" />
        <div v-if="tab !== 3" class="placeholder" />
        <v-btn
          v-if="tab === 3"
          icon
          color="error"
          :disabled="loading"
          :loading="loading"
          @click="downloadInvoicesZip"
          class="download-button ml-3"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>
      </div>
    </v-tabs>

    <v-card outlined>
      <v-tabs-items v-model="tab">
        <v-tab-item>
          <AdminPendingInvoices />
        </v-tab-item>
        <v-tab-item>
          <AdminAwaitingApprovalInvoices @retryTransfer="retryTransfer" />
        </v-tab-item>
        <v-tab-item>
          <AdminRefusedInvoices @retryTransfer="retryTransfer" />
        </v-tab-item>
        <v-tab-item>
          <AdminApprovedInvoices
            @selectedInvoices="setSelectedInvoices"
            @retryTransfer="retryTransfer"
            ref="approvedInvoices"
          />
        </v-tab-item>
        <v-tab-item>
          <AdminTransfersInvoices />
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <AdminInvoiceDetails />
    <ShowTotalSelectedInvoices v-show="showPayInfo" :invoices="invoicesToPay" />
    <ModalApproveRefuseInvoice />
    <ModalRemoveInvoice />
    <ModalPayInvoices @resetSelectedInvoices="resetSelectedInvoices" :invoices="invoicesToPay" />
    <TransferConfirmModal
      :parsedTransfers="parsedTransfers"
      :selectedTransfers="selectedTransfers"
      :refresh="fetchAdminInvoices"
    />
  </div>
</template>

<script>
import store from '@/store'
import moment from 'moment-timezone'
import { mapActions, mapMutations, mapGetters } from 'vuex'

import AdminPendingInvoices from './components/AdminPendingInvoices'
import AdminAwaitingApprovalInvoices from './components/AdminAwaitingApprovalInvoices'
import AdminRefusedInvoices from './components/AdminRefusedInvoices'
import AdminApprovedInvoices from './components/AdminApprovedInvoices'
import AdminTransfersInvoices from './components/AdminTransfersInvoices'

import AdminInvoicesFilters from './components/filters/AdminInvoicesFilters'
import AdminTransfersFilters from './components/filters/AdminTransfersFilters'
import AdminPendingInvoicesFilters from './components/filters/AdminPendingInvoicesFilters'

import AdminInvoiceDetails from './components/AdminInvoiceDetails'

import ShowTotalSelectedInvoices from './components/fragments/ShowTotalSelectedInvoices'
import AdminPayInvoicesButton from './components/fragments/AdminPayInvoicesButton'

import ModalApproveRefuseInvoice from './modals/ModalApproveRefuseInvoice'
import ModalRemoveInvoice from './modals/ModalRemoveInvoice'
import ModalPayInvoices from './modals/ModalPayInvoices'

import TransferConfirmModal from '@/components/transfer/modal/TransferConfirmModal'

import { userHasSomeRole } from '@/util/helpers'

export default {
  name: 'AdminHostsInvoices',

  components: {
    AdminPendingInvoices,
    AdminAwaitingApprovalInvoices,
    AdminRefusedInvoices,
    AdminApprovedInvoices,
    AdminTransfersInvoices,

    AdminInvoicesFilters,
    AdminTransfersFilters,
    AdminPendingInvoicesFilters,

    AdminInvoiceDetails,

    ShowTotalSelectedInvoices,
    AdminPayInvoicesButton,

    ModalApproveRefuseInvoice,
    ModalRemoveInvoice,
    ModalPayInvoices,

    TransferConfirmModal,
  },

  computed: {
    ...mapGetters(['transfer']),

    showPendingInvoiceFilters() {
      return this.tab === 0
    },

    showTransferFilters() {
      return this.tab === 4
    },

    showInvoiceFilters() {
      return this.tab !== 0 && this.tab !== 4
    },

    showPayInfo() {
      return this.canPayInvoices && this.invoicesToPay.length > 0 && this.tab === 3
    },

    canPayInvoices() {
      return !userHasSomeRole(['ROLE_PARTNER'])
    },
  },

  methods: {
    ...mapMutations(['setFilters', 'setIsDrawerOpen', 'setCurrentPage', 'setAdminInvoices', 'setTotalPages']),
    ...mapActions(['fetchAdminInvoices', 'fetchTransfers', 'fetchTransfer', 'openModal']),

    applyRouterTabParam(tabName) {
      this.$router.push({ query: { ...this.$route.query, tab: tabName } }).catch((err) => {
        if (err.name !== 'NavigationDuplicated') return
      })
    },

    resetFilterItems() {
      this.$nextTick(() => {
        const filters = this.$refs?.adminInvoicesFilters

        if (!filters) return

        const filtersValues = filters?.$refs.filtersValues

        if (filters) filters.resetValues()
        if (filtersValues) filtersValues.showAppliedFilters()
      })
    },

    setDefaultDateLabel() {
      this.$nextTick(() => {
        const filters = this.$refs?.adminInvoicesFilters

        if (!filters) return

        const filterDate = filters.$refs?.filterDate

        if (filterDate) filterDate?.setDateLabel('recent')
      })
    },

    setRecentDateRange() {
      const todayFirstHour = moment().format('YYYY-MM-DD') + 'T00:00:00'
      const todayLastHour = moment().format('YYYY-MM-DD') + 'T23:59:59'

      const startDate = moment.tz(todayFirstHour, this.timezone).add(-1, 'month').format('YYYY-MM-DDTHH:mm:ssZ')
      const endDate = moment.tz(todayLastHour, this.timezone).add(1, 'month').format('YYYY-MM-DDTHH:mm:ssZ')

      return { creationStartDate: startDate, creationEndDate: endDate }
    },

    resetInvoicesListAndFilters() {
      this.setFilters({})
      this.setTotalPages(0)
      this.setCurrentPage(1)
      this.resetFilterItems()
      this.setAdminInvoices([])
      this.applyRouterTabParam('PENDING')
      this.$nextTick(() => this.setDefaultDateLabel())
    },

    fetchPending() {
      resolve(this.resetInvoicesListAndFilters()).then(() => {
        const date = new Date().toString()
        this.$nextTick(() => {
          const dateFilterComponent = this.$refs.adminPendingInvoicesFilters
          dateFilterComponent?.mountDateFilterParams(date)
        })
      })
    },

    fetchInvoices(status) {
      this.resetInvoicesListAndFilters()
      this.applyRouterTabParam(status)
      this.setFilters({
        ...{ hasTransfer: false },
        ...this.setRecentDateRange(),
        status
      })
      this.fetchAdminInvoices()
    },

    transfers() {
      this.resetInvoicesListAndFilters()
      this.setFilters({ status: 'COMPLETED' })
      this.applyRouterTabParam('PAID')
      this.fetchTransfers()
    },

    setSelectedInvoices(invoices) {
      this.invoicesToPay = invoices
    },

    resetSelectedInvoices() {
      this.invoicesToPay = []
      this.selectedTransfers = []

      const approvedInvoices = this.$refs.approvedInvoices
      approvedInvoices.selected = []
    },

    async downloadInvoicesZip() {
      this.loading = true
      try {
        await this.$refs.approvedInvoices.downloadInvoicesZip()
      } finally {
        this.loading = false
      }
    },

    retryTransfer(invoice) {
      if (!invoice?.id) return
      this.fetchTransfer(invoice.id)
        .then(() => {
          this.selectedTransfers = [this.transfer]
          this.parsedTransfers = [invoice.id]
        })
        .then(() => this.openModal('TRANSFER_CONFIRM_MODAL'))
    }
  },

  data() {
    return {
      tab: 0,
      invoicesToPay: [],
      parsedTransfers: [],
      selectedTransfers: [],
      timezone: moment.tz.guess(),
      loading: false
    }
  }
}
</script>

<style scoped>
.tabs__filters {
  display: flex;
  align-items: center;
  padding: 8px;
  overflow: visible;
}

.placeholder {
  width: 5px;
}
</style>